package one.healthium.onboarding.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Outline
import androidx.compose.ui.graphics.Paint
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawOutline
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Fill
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.toSize
import androidx.compose.ui.util.fastForEachIndexed
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.char
import kotlinx.datetime.toLocalDateTime
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.localizedMonthNames
import one.healthium.domain.user.models.PrognoseWeightModel
import one.healthium.loco.resolve
import one.healthium.onboarding.ui.utils.createShadowPaint
import one.healthium.onboarding.ui.utils.drawShadow
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_4
import one.healthium.ui.compose.theme.green_8
import one.healthium.ui.compose.theme.orange_4
import one.healthium.ui.compose.theme.red_5
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.yellow_7
import kotlin.math.abs
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days

/**
 * UI model for prognose weight chart with pre-resolved strings.
 * All LString values should be resolved before creating this model.
 */
internal data class PrognoseWeightUiModel(
    val weightText: String,
    val periodText: String,
)

// Defaults
// overall
val horizontalPadding = 25.dp
val verticalStartPadding = 30.dp
val verticalEndPadding = 30.dp

// grid
val verticalLineColor = Color(0xccf2f2f2)
val verticalLineWidth = 2.dp
val legendSpacing = 8.dp


// graph
val chartLineWidth = 4.dp
val graphBrush = Brush.linearGradient(
    0f to red_5,
    0.34f to orange_4,
    0.73f to yellow_7,
    1f to green_8,
)

// points
val pointRadius = 6.dp
val pointBorderWidth = 2.dp
val lastPointRadius = 7.dp
val lastPointBorderWidth = 14.dp
val pointColors = listOf(
    red_5,
    orange_4,
    yellow_7,
    green_8,
)

val tooltipTriangleHeight = 14.dp
val tooltipTriangleWidth = 18.dp
val tooltipPadding = 6.dp

@Composable
internal fun PrognoseWeightChart(
    modifier: Modifier = Modifier,
    prognoseWeight: List<PrognoseWeightModel>,
    isMetric: Boolean,
) {
    val textMeasurer = rememberTextMeasurer()

    val legendTextStyle = AppTheme.textStyles.body2

    val pointBorderColor = MaterialTheme.colorScheme.background
    val pointLegendTextStyle = AppTheme.textStyles.body1Bold
    val lastPointLegendTextStyle = AppTheme.textStyles.subTitleBold

    // Resolve LStrings in composable context (lightweight string resource lookups)
    val resolvedWeights = prognoseWeight.map { it.weight.formatWeight(isMetric).resolve() }

    // Memoize UI model: toDayNames() with Clock.System.now() + date formatting only runs when data changes
    val prognoseWeightUi = remember(resolvedWeights) {
        prognoseWeight.mapIndexed { index, model ->
            PrognoseWeightUiModel(
                weightText = resolvedWeights[index],
                periodText = toDayNames(model),
            )
        }
    }

    // Pre-compute all TextMeasurer results (avoid repeated text shaping in DrawScope)
    val legendMeasurements = remember(prognoseWeightUi, legendTextStyle) {
        prognoseWeightUi.map { textMeasurer.measure(it.periodText, legendTextStyle) }
    }
    val pointWeightMeasurements = remember(prognoseWeightUi, pointLegendTextStyle) {
        prognoseWeightUi.dropLast(1).map { textMeasurer.measure(it.weightText, pointLegendTextStyle) }
    }
    val goalMeasurement = remember(prognoseWeightUi, lastPointLegendTextStyle) {
        prognoseWeightUi.lastOrNull()?.let {
            textMeasurer.measure(
                text = "Goal\n${it.weightText}", // TODO loco
                style = lastPointLegendTextStyle.copy(textAlign = TextAlign.Center),
            )
        }
    }

    // Cache shadow Paint once (BlurMaskFilter is expensive to create per-frame)
    val shadowPaint = remember { createShadowPaint(blurRadiusPx = 4f) }

    Canvas(modifier = modifier) {
        val points = drawGridAndCalculatePointsCoordinates(legendMeasurements)
        if (points.isEmpty()) return@Canvas

        drawPath(
            path = quadCurvedPathWithPoints(points),
            brush = graphBrush,
            style = Stroke(width = chartLineWidth.toPx()),
        )

        drawPoints(
            points = points,
            pointWeightMeasurements = pointWeightMeasurements,
            goalMeasurement = goalMeasurement,
            pointBorderColor = pointBorderColor,
            shadowPaint = shadowPaint,
        )
    }
}

private fun DrawScope.drawPoints(
    points: List<Offset>,
    pointWeightMeasurements: List<TextLayoutResult>,
    goalMeasurement: TextLayoutResult?,
    pointBorderColor: Color,
    shadowPaint: Paint,
) {
    points.fastForEachIndexed { index, point ->
        val pointColor = pointColors.getOrNull(index) ?: pointColors.last()

        // last item should be bigger and bold
        if (index == points.size - 1 && goalMeasurement != null) {
            val borderRadius = lastPointBorderWidth.toPx() + 4.dp.toPx()

            val topLeftText = Offset(
                point.x - (goalMeasurement.size.width / 2),
                point.y - legendSpacing.toPx() - borderRadius - goalMeasurement.size.height - tooltipTriangleHeight.toPx(),
            )
            val topLeftTooltip = topLeftText.minus(Offset(tooltipPadding.toPx(), tooltipPadding.toPx()))
            val tooltipSize = Size(
                goalMeasurement.size.width + tooltipPadding.times(2).toPx(),
                goalMeasurement.size.height + tooltipPadding.times(2).toPx(),
            )
            drawRoundRect(
                color = pointColor,
                topLeft = topLeftTooltip,
                size = tooltipSize,
                cornerRadius = CornerRadius(tooltipPadding.toPx(), tooltipPadding.toPx()),
            )
            drawTriangle(
                offset = topLeftTooltip + Offset(
                    (tooltipSize.width - tooltipTriangleWidth.toPx()) / 2,
                    tooltipSize.height - 3.dp.toPx()
                ),
                size = Size(tooltipTriangleWidth.toPx(), tooltipTriangleHeight.toPx()),
                color = pointColor,
            )
            drawText(
                textLayoutResult = goalMeasurement,
                color = white,
                topLeft = topLeftText
            )

            drawCircleWithShadow(
                color = pointBorderColor,
                radius = borderRadius,
                center = point,
                shadowPaint = shadowPaint,
            )
            drawCircle(
                color = pointColor.copy(alpha = 0.2f),
                radius = lastPointBorderWidth.toPx(),
                center = point,
            )
            drawCircle(
                color = pointColor,
                radius = lastPointRadius.toPx(),
                center = point,
            )
        } else if (index < pointWeightMeasurements.size) {
            val borderRadius = pointRadius.toPx() + pointBorderWidth.toPx()
            val measuredText = pointWeightMeasurements[index]
            val topLeftText = Offset(
                point.x - (measuredText.size.width / 2),
                point.y - legendSpacing.toPx() - borderRadius - measuredText.size.height,
            )
            drawRect(
                color = pointBorderColor,
                topLeft = topLeftText,
                size = measuredText.size.toSize(),
            )
            drawText(
                textLayoutResult = measuredText,
                color = pointColor,
                topLeft = topLeftText
            )

            drawCircleWithShadow(
                color = pointBorderColor,
                radius = borderRadius,
                center = point,
                shadowPaint = shadowPaint,
            )
            drawCircle(
                color = pointColor,
                radius = pointRadius.toPx(),
                center = point,
            )
        }
    }
}

private fun DrawScope.drawGridAndCalculatePointsCoordinates(
    legendMeasurements: List<TextLayoutResult>,
): MutableList<Offset> {
    val count = legendMeasurements.size
    if (count <= 1) return mutableListOf()

    val width = size.width
    val height = size.height

    val points = mutableListOf<Offset>()

    val verticalLinesSpace = (width - (2 * horizontalPadding.toPx())) / (count - 1)
    var startOffset = horizontalPadding.toPx()
    val graphAvailableHeight = height - verticalStartPadding.toPx() - verticalEndPadding.toPx()
    val pointsStep = graphAvailableHeight / count

    legendMeasurements.fastForEachIndexed { index, measuredText ->
        points.add(Offset(startOffset, pointsStep * (index + 1)))

        drawText(
            textLayoutResult = measuredText,
            topLeft = Offset(
                startOffset - measuredText.size.width / 2,
                height - measuredText.size.height,
            ),
            color = gray_4,
        )

        drawLine(
            color = verticalLineColor,
            start = Offset(startOffset, 0f),
            end = Offset(
                startOffset,
                height - measuredText.size.height - legendSpacing.toPx(),
            ),
            strokeWidth = verticalLineWidth.toPx(),
        )

        startOffset += verticalLinesSpace
    }
    return points
}

fun DrawScope.drawTriangle(offset: Offset, size: Size, color: Color) {
    val rect = Rect(offset, size)
    val trianglePath = Path().apply {
        moveTo(rect.topLeft.x, rect.topLeft.y)
        lineTo(rect.topRight.x, rect.topRight.y)
        lineTo(rect.bottomCenter.x, rect.bottomCenter.y)
        close()
    }

    drawIntoCanvas { canvas ->
        canvas.drawOutline(
            outline = Outline.Generic(trianglePath),
            paint = Paint().apply {
                this.color = color
                pathEffect = PathEffect.cornerPathEffect(rect.maxDimension / 3)
            }
        )
    }
}


private fun toDayNames(it: PrognoseWeightModel) =
    if (it.inDays == 0) {
        "Today"
    } else {
        Clock.System.now().plus(it.inDays.days).toLocalDateTime(TimeZone.currentSystemDefault())
            .format(
                LocalDateTime.Format {
                    monthName(localizedMonthNames(abbreviated = true))
                    char(' ')
                    day()
                }
            )
    }

// TODO update graph
private fun quadCurvedPathWithPoints(points: List<Offset>): Path {
    val path = Path()

    if (points.isNotEmpty()) {
        path.moveTo(points.first().x, points.first().y)
    }

    if (points.size == 2) {
        path.lineTo(points.last().x, points.last().y)
    }

    var prevPoint = points.first()
    points.drop(1).forEach { point ->
        val midPoint = mid(prevPoint, point)

        val controlPoint1 = controlPointForPoints(midPoint, prevPoint)
        path.quadraticTo(
            x1 = controlPoint1.x,
            y1 = controlPoint1.y,
            x2 = midPoint.x,
            y2 = midPoint.y
        )

        val controlPoint2 = controlPointForPoints(midPoint, point)
        path.quadraticTo(
            x1 = controlPoint2.x,
            y1 = controlPoint2.y,
            x2 = point.x,
            y2 = point.y
        )

        prevPoint = point
    }

    return path
}

private fun mid(point1: Offset, point2: Offset): Offset {
    return Offset((point1.x + point2.x) / 2, (point1.y + point2.y) / 2)
}

fun controlPointForPoints(p1: Offset, p2: Offset): Offset {
    val controlPoint = mid(p1, p2)
    val diffY = abs(p2.y - controlPoint.y)
    return if (p1.y < p2.y) {
        controlPoint.copy(y = controlPoint.y + diffY)
    } else if (p1.y > p2.y) {
        controlPoint.copy(y = controlPoint.y - diffY)
    } else {
        controlPoint
    }
}

private fun DrawScope.drawCircleWithShadow(
    center: Offset,
    radius: Float,
    color: Color,
    shadowPaint: Paint,
) {
    val path = Path().apply {
        addOval(Rect(center, radius))
    }
    drawShadow(path = path, paint = shadowPaint)
    drawPath(
        path = path,
        color = color,
        style = Fill,
    )
}
